// ============================================================================
// Statuts de facturation électronique (réforme française, PDP BC Solutions).
//
// PORTAGE de l'app mobile — utils/api/user.api.ts dans fast-facture-mobile.
// Les libellés, les couleurs et les règles doivent rester IDENTIQUES dans les
// deux applications : ce sont les termes de la plateforme, ceux qu'un
// utilisateur retrouve dans l'app et que son comptable lit ici. Toute
// modification se fait dans les deux dépôts.
// ============================================================================

export type BcsInfo = { label: string; color: string; detail?: string };

// Statuts qui annulent la portée comptable d'une facture : refusée par le
// destinataire (210) ou rejetée par la plateforme (213). Elle ne sera ni
// encaissée ni déductible. Un litige (207) n'en fait PAS partie : la créance
// existe toujours, elle est seulement contestée.
export const BCS_VOID_STATUSES = [210, 213];

export function isBcsVoided(d: { bcsLifecycleStatus?: number }): boolean {
  return BCS_VOID_STATUSES.includes(d.bcsLifecycleStatus ?? 0);
}

// Un SEUL jeu de libellés pour une facture émise et une dépense reçue, repris
// mot pour mot de la plateforme. On n'affiche jamais le code FNFE.
// 201 et 202 restent volontairement vagues : leur correspondance exacte n'est
// pas documentée, « transmission en cours » est vrai dans tous les cas.
const BCS_LABELS: Record<number, string> = {
  200: "Soumise à la plateforme",
  201: "Transmission en cours",
  202: "Transmission en cours",
  203: "Mise à disposition",
  204: "Prise en charge",
  205: "Approuvée",
  206: "Approuvée partiellement",
  207: "En litige",
  208: "Suspendue",
  209: "Complétée",
  210: "Refusée",
  211: "Paiement transmis",
  212: "Encaissée",
  213: "Rejetée par la plateforme",
};

// Phrase explicative : les termes de la plateforme sont exacts mais parfois
// muets (« Suspendue » ne dit pas ce qu'on attend).
const BCS_DETAILS: Record<number, string> = {
  206: "Une partie seulement du montant est acceptée.",
  207: "Le contenu ou le montant de la facture est contesté.",
  208: "Des justificatifs sont attendus avant la reprise du traitement.",
  209: "Les justificatifs attendus ont été transmis.",
  210: "La facture doit être annulée par un avoir ou corrigée.",
  211: "Le paiement a été annoncé, l'encaissement n'est pas encore confirmé.",
  212: "Le règlement est confirmé encaissé.",
  213: "La plateforme a rejeté le dépôt : la facture n'a pas été transmise.",
};

// Couleur commune aux deux points de vue : le sens (bloquant / en cours /
// abouti) ne dépend pas de qui regarde.
export function getBcsStatusColor(code: number): string {
  if ([207, 208, 210, 213].includes(code)) return "#c0392b";
  // Les étapes de transmission (200…203) ne sont pas un aboutissement.
  if (code <= 203) return "#c8761b";
  return "#1a8a4f";
}

export function getBcsLifecycleLabel(code?: number): string | undefined {
  if (!code) return undefined;
  return BCS_LABELS[code] ?? "Statut mis à jour";
}

export function getBcsLifecycleDetail(code?: number): string | undefined {
  return code ? BCS_DETAILS[code] : undefined;
}

// Statut d'une facture ÉMISE. undefined si elle n'est jamais passée par la
// facturation électronique (client particulier, modèle non activé…).
export function getFactureBcsInfo(f: {
  bcsFlowId?: string;
  bcsAckStatus?: string;
  bcsAckReasonMessage?: string | null;
  bcsLifecycleStatus?: number;
  bcsBlocked?: { message: string } | null;
}): BcsInfo | undefined {
  // Émise au client mais jamais transmise à la plateforme : c'est la seule
  // situation qui demande une action immédiate, et aucun flux n'existe.
  if (f.bcsBlocked && !f.bcsFlowId) {
    return {
      label: "Non transmise à la plateforme",
      color: "#c0392b",
      detail: f.bcsBlocked.message,
    };
  }
  if (!f.bcsFlowId) return undefined;
  if (f.bcsLifecycleStatus) {
    const code = f.bcsLifecycleStatus;
    return {
      label: getBcsLifecycleLabel(code)!,
      color: getBcsStatusColor(code),
      detail: getBcsLifecycleDetail(code),
    };
  }
  if (f.bcsAckStatus === "Rejected") {
    return {
      label: "Rejetée par la plateforme",
      color: "#c0392b",
      detail: f.bcsAckReasonMessage || undefined,
    };
  }
  if (f.bcsAckStatus === "Ok") {
    return { label: "Transmise au destinataire", color: "#2a5bd7" };
  }
  return { label: "Transmission en cours…", color: "#c8761b" };
}

// Statut d'une DÉPENSE reçue. Rien pour une dépense saisie à la main : le
// cycle de vie n'existe que pour les factures arrivées par la plateforme.
export function getDepenseBcsInfo(d: {
  source?: string;
  bcsFlowId?: string;
  bcsLifecycleStatus?: number;
}): BcsInfo | undefined {
  if (d.source !== "bcs" || !d.bcsFlowId) return undefined;
  // Aucun statut encore reçu : la facture vient d'arriver.
  const code = d.bcsLifecycleStatus ?? 203;
  return {
    label: getBcsLifecycleLabel(code)!,
    color: getBcsStatusColor(code),
    detail: getBcsLifecycleDetail(code),
  };
}

// --- Filtres de tableau -----------------------------------------------------
// Les valeurs filtrées sont les libellés eux-mêmes (le selectFilter compare en
// égalité stricte de chaînes). Une facture hors périmètre reçoit une valeur
// dédiée pour rester filtrable.
export const HORS_BCS = "Hors facturation électronique";

const uniq = (l: string[]) => Array.from(new Set(l));
const toOptions = (l: string[]) =>
  Object.fromEntries(l.map((v) => [v, v])) as Record<string, string>;

const LIFECYCLE_LABELS = uniq(Object.values(BCS_LABELS));

export const FACTURE_BCS_FILTER_OPTIONS = toOptions([
  "Non transmise à la plateforme",
  "Transmission en cours…",
  "Transmise au destinataire",
  ...LIFECYCLE_LABELS,
  HORS_BCS,
]);

export const DEPENSE_BCS_FILTER_OPTIONS = toOptions([
  ...LIFECYCLE_LABELS,
  HORS_BCS,
]);
